import { useAppSelector, useContactDetails } from "../../../hooks";

import {
  formatDurationToDays,
  formatDurationToMonths,
  getCurrency,
  useCreatePaymentMethodMutation,
  useListPaymentMethodsQuery,
} from "@stepapp/core";
import { Block, LinePlaceholder, Modal } from "@stepapp/ui";
import { PaymentRequestButtonElement, useStripe } from "@stripe/react-stripe-js";
import { PaymentRequest } from "@stripe/stripe-js";
import {
  ApplePayDeferredPaymentRequest,
  ApplePayRecurringPaymentRequest,
} from "@stripe/stripe-js/dist/stripe-js/elements/apple-pay";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ScrollView } from "react-native";
import { AppStorage } from "../../../services/AppStorage";
import PaymentOption from "../PaymentOption";

type Props = {
  paymentMethodId?: string;
  selectPaymentMethod: (paymentMethodId: string) => void;
  setLoading: (v: boolean) => void;
  cycleDuration?: string;
  price?: number | null;
  startDate?: string | null;
  bundleName?: string;
};

// For web only
export const PaymentMethods = ({
  paymentMethodId = "",
  selectPaymentMethod,
  cycleDuration,
  price,
  startDate,
  bundleName,
}: Props) => {
  const { t } = useTranslation();
  const id = useAppSelector(({ user }) => user.id);
  const { AppUrl } = useContactDetails();
  const stripe = useStripe();
  const countryCode = AppStorage().get("countryCode");
  const country = countryCode === " NO" ? "NO" : "PL";
  const currency = getCurrency(country).toLowerCase();

  const [failedCardModal, setFailedCardModal] = useState(false);
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);

  const { data, loading: loadingPaymentMethods } = useListPaymentMethodsQuery({
    skip: !id,
  });

  const [createPaymentMethod] = useCreatePaymentMethodMutation();

  const durationDays = formatDurationToDays(cycleDuration || "0");
  const durationMonths = formatDurationToMonths(cycleDuration || "0");
  const monthly = durationDays >= 30;

  const recurringPaymentRequest = useMemo(() => {
    return {
      managementURL: `${AppUrl}payments`,
      paymentDescription: bundleName || "",
      regularBilling: {
        amount: price || 0,
        label: bundleName || "",
        recurringPaymentIntervalUnit: monthly ? "month" : "day",
        recurringPaymentStartDate: dayjs(startDate)
          .add(startDate ? 0 : 1, "day")
          .toDate(),
        recurringPaymentIntervalCount: monthly ? durationMonths : durationDays,
      },
    } satisfies ApplePayRecurringPaymentRequest;
  }, [AppUrl, price, monthly, bundleName, startDate, durationMonths, durationDays]);

  const deferredPaymentRequest = useMemo(() => {
    return {
      paymentDescription: bundleName || "",
      managementURL: `${AppUrl}payments`,
      deferredBilling: {
        amount: price || 0,
        label: bundleName || "",
        deferredPaymentDate: dayjs(startDate)
          .add(startDate ? 0 : 1, "day")
          .toDate(),
      },
    } satisfies ApplePayDeferredPaymentRequest;
  }, [AppUrl, price, bundleName, startDate]);

  useEffect(() => {
    if (!stripe) return;

    const total = {
      amount: price || 0,
      label: bundleName || "",
    };

    const applePayProps =
      durationDays >= 1 ? { recurringPaymentRequest } : { deferredPaymentRequest };

    const request = stripe?.paymentRequest({
      country,
      currency,
      total,
      applePay: applePayProps,
    });

    request?.canMakePayment().then((result) => {
      if (result) {
        setPaymentRequest(request);
      }
    });

    request?.on("token", async (e) => {
      const { data } = await createPaymentMethod({
        variables: { paymentMethodToken: e?.token?.id },
      });

      if (data?.createPaymentMethod) {
        selectPaymentMethod(data?.createPaymentMethod || "");
      } else {
        setFailedCardModal(true);
      }
    });
  }, [
    stripe,
    deferredPaymentRequest,
    recurringPaymentRequest,
    durationDays,
    bundleName,
    country,
    currency,
    price,
    createPaymentMethod,
    selectPaymentMethod,
  ]);

  return (
    <ScrollView>
      {paymentRequest && <PaymentRequestButtonElement options={{ paymentRequest }} />}

      {loadingPaymentMethods && data?.listPaymentMethods?.length === 0 && (
        <Block marginBottom={2}>
          <LinePlaceholder height={5} />
        </Block>
      )}
      {data?.listPaymentMethods?.map(({ id, brand, last4 }) => (
        <PaymentOption
          icon={paymentMethodId === id ? "done" : undefined}
          key={id}
          label={last4}
          onPress={() => selectPaymentMethod(id)}
          type={brand as "visa"}
        />
      ))}

      <Modal
        description={t("pages.chooseCard.addCardFailedDescription")}
        primaryAction={() => setFailedCardModal(false)}
        primaryActionLabel={t("common.goBack")}
        title={t("pages.chooseCard.addCardFailed")}
        toggle={() => setFailedCardModal(false)}
        visible={failedCardModal}
      />
    </ScrollView>
  );
};
